import React, { useState, useEffect } from 'react';
import { DashboardWidget, CreateWidgetRequest } from '../../services/dashboardConfigurationService';
import './WidgetConfigurationModal.css';

interface KPIOption {
  code: string;
  name: string;
  category: string;
  subcategory?: string;
  units?: string;
  description?: string;
}

interface WidgetConfigurationModalProps {
  isOpen: boolean;
  widget?: DashboardWidget | null;
  availableKPIs: KPIOption[];
  availableCalculations: Array<{ id: string; name: string; description: string }>;
  colorPresets: Array<{ id: string; name: string; colors: string[] }>;
  onSave: (widgetData: CreateWidgetRequest) => void;
  onClose: () => void;
}

const WidgetConfigurationModal: React.FC<WidgetConfigurationModalProps> = ({
  isOpen,
  widget,
  availableKPIs,
  availableCalculations,
  colorPresets,
  onSave,
  onClose
}) => {
  const [formData, setFormData] = useState<CreateWidgetRequest>({
    widgetName: '',
    widgetType: 'chart',
    chartType: 'bar',
    positionX: 0,
    positionY: 0,
    width: 4,
    height: 3,
    widgetConfig: {},
    dataConfig: {
      kpiCodes: [],
      calculationType: 'raw',
      calculationId: undefined,
      timeRange: 'last_3_months',
      locationFilters: [],
      aggregationType: 'sum',
      groupBy: 'none',
      filters: {}
    }
  });

  const [selectedKPIs, setSelectedKPIs] = useState<string[]>([]);
  const [selectedCalculation, setSelectedCalculation] = useState<string>('');
  const [selectedColorPreset, setSelectedColorPreset] = useState<string>('');

  useEffect(() => {
    if (widget) {
      // Edit mode - populate form with existing widget data
      setFormData({
        widgetName: widget.widgetName,
        widgetType: widget.widgetType,
        chartType: widget.chartType,
        positionX: widget.positionX,
        positionY: widget.positionY,
        width: widget.width,
        height: widget.height,
        widgetConfig: widget.widgetConfig || {},
        dataConfig: widget.dataConfig || {
          kpiCodes: [],
          calculationType: 'raw',
          calculationId: undefined,
          timeRange: 'last_3_months',
          locationFilters: [],
          aggregationType: 'sum',
          groupBy: 'none',
          filters: {}
        }
      });
      setSelectedKPIs(widget.dataConfig?.kpiCodes || []);
      setSelectedCalculation(widget.dataConfig?.calculationId?.toString() || '');
    } else {
      // Create mode - reset form
      setFormData({
        widgetName: '',
        widgetType: 'chart',
        chartType: 'bar',
        positionX: 0,
        positionY: 0,
        width: 4,
        height: 3,
        widgetConfig: {},
        dataConfig: {
          kpiCodes: [],
          calculationType: 'raw',
          calculationId: undefined,
          timeRange: 'last_3_months',
          locationFilters: [],
          aggregationType: 'sum',
          groupBy: 'none',
          filters: {}
        }
      });
      setSelectedKPIs([]);
      setSelectedCalculation('');
      setSelectedColorPreset('');
    }
  }, [widget, isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDataConfigChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      dataConfig: {
        ...prev.dataConfig,
        [field]: value
      }
    }));
  };

  const handleKPIToggle = (kpiCode: string) => {
    const newKPIs = selectedKPIs.includes(kpiCode) 
      ? selectedKPIs.filter(k => k !== kpiCode)
      : [...selectedKPIs, kpiCode];
    
    setSelectedKPIs(newKPIs);
    handleDataConfigChange('kpiCodes', newKPIs);
  };

  const handleCalculationChange = (calculation: string) => {
    setSelectedCalculation(calculation);
    handleDataConfigChange('calculationId', calculation ? parseInt(calculation) : undefined);
    handleDataConfigChange('calculationType', calculation ? 'custom' : 'raw');
  };

  const handleColorPresetChange = (presetId: string) => {
    setSelectedColorPreset(presetId);
    const preset = colorPresets.find(p => p.id === presetId);
    if (preset) {
      setFormData(prev => ({
        ...prev,
        widgetConfig: {
          ...prev.widgetConfig,
          colorPreset: presetId,
          colors: preset.colors
        }
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  const chartTypes = [
    { value: 'bar', label: 'Bar Chart' },
    { value: 'line', label: 'Line Chart' },
    { value: 'pie', label: 'Pie Chart' },
    { value: 'doughnut', label: 'Doughnut Chart' },
    { value: 'area', label: 'Area Chart' },
    { value: 'scatter', label: 'Scatter Plot' },
    { value: 'radar', label: 'Radar Chart' },
    { value: 'bubble', label: 'Bubble Chart' },
    { value: 'horizontalBar', label: 'Horizontal Bar' }
  ];

  const widgetTypes = [
    { value: 'chart', label: 'Chart' },
    { value: 'metric_card', label: 'Metric Card' },
    { value: 'table', label: 'Table' },
    { value: 'insight_card', label: 'Insight Card' },
    { value: 'kpi_summary', label: 'KPI Summary' }
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-container widget-config-modal">
        <div className="modal-header">
          <h2>{widget ? 'Edit Widget' : 'Create New Widget'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-content">
          {/* Basic Configuration */}
          <div className="config-section">
            <h3>Basic Configuration</h3>
            
            <div className="form-group">
              <label htmlFor="widgetName">Widget Name</label>
              <input
                id="widgetName"
                type="text"
                value={formData.widgetName}
                onChange={(e) => handleInputChange('widgetName', e.target.value)}
                placeholder="Enter widget name"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="widgetType">Widget Type</label>
                <select
                  id="widgetType"
                  value={formData.widgetType}
                  onChange={(e) => handleInputChange('widgetType', e.target.value)}
                >
                  {widgetTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.widgetType === 'chart' && (
                <div className="form-group">
                  <label htmlFor="chartType">Chart Type</label>
                  <select
                    id="chartType"
                    value={formData.chartType || 'bar'}
                    onChange={(e) => handleInputChange('chartType', e.target.value)}
                  >
                    {chartTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Position and Size */}
          <div className="config-section">
            <h3>Position & Size</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="positionX">Position X</label>
                <input
                  id="positionX"
                  type="number"
                  min="0"
                  max="11"
                  value={formData.positionX}
                  onChange={(e) => handleInputChange('positionX', parseInt(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="positionY">Position Y</label>
                <input
                  id="positionY"
                  type="number"
                  min="0"
                  value={formData.positionY}
                  onChange={(e) => handleInputChange('positionY', parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="width">Width (columns)</label>
                <input
                  id="width"
                  type="number"
                  min="1"
                  max="12"
                  value={formData.width}
                  onChange={(e) => handleInputChange('width', parseInt(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="height">Height (rows)</label>
                <input
                  id="height"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.height}
                  onChange={(e) => handleInputChange('height', parseInt(e.target.value))}
                />
              </div>
            </div>
          </div>

          {/* Data Configuration */}
          <div className="config-section">
            <h3>Data Configuration</h3>
            
            {/* KPI Selection */}
            <div className="form-group">
              <label>Select KPIs</label>
              {availableKPIs.length === 0 ? (
                <div className="no-kpis-message">
                  <p>No KPIs available. Please check your permissions or contact your administrator.</p>
                </div>
              ) : (
                <div className="kpi-categories">
                  {(() => {
                    // Group KPIs by category
                    const kpisByCategory = availableKPIs.reduce((acc, kpi) => {
                      const category = kpi.category || 'Other';
                      if (!acc[category]) {
                        acc[category] = [];
                      }
                      acc[category].push(kpi);
                      return acc;
                    }, {} as Record<string, KPIOption[]>);

                    return Object.entries(kpisByCategory).map(([category, kpis]) => (
                      <div key={category} className="kpi-category">
                        <h4 className="kpi-category-title">{category}</h4>
                        <div className="kpi-grid">
                          {kpis.map(kpi => (
                            <label key={kpi.code} className="kpi-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedKPIs.includes(kpi.code)}
                                onChange={() => handleKPIToggle(kpi.code)}
                              />
                              <div className="kpi-info">
                                <span className="kpi-name">{kpi.name}</span>
                                <span className="kpi-code">({kpi.code})</span>
                                {kpi.units && <span className="kpi-units">[{kpi.units}]</span>}
                                {kpi.description && (
                                  <span className="kpi-description" title={kpi.description}>
                                    ℹ️
                                  </span>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Calculation Selection */}
            <div className="form-group">
              <label htmlFor="calculation">Calculation</label>
              <select
                id="calculation"
                value={selectedCalculation}
                onChange={(e) => handleCalculationChange(e.target.value)}
              >
                <option value="">Select calculation...</option>
                {availableCalculations.map(calc => (
                  <option key={calc.id} value={calc.id}>
                    {calc.name}
                  </option>
                ))}
              </select>
              {selectedCalculation && (
                <small className="calculation-description">
                  {availableCalculations.find(c => c.id === selectedCalculation)?.description}
                </small>
              )}
            </div>

            {/* Time Range */}
            <div className="form-group">
              <label htmlFor="timeRange">Time Range</label>
              <select
                id="timeRange"
                value={formData.dataConfig.timeRange}
                onChange={(e) => handleDataConfigChange('timeRange', e.target.value)}
              >
                <option value="last_3_months">Last 3 Months</option>
                <option value="last_6_months">Last 6 Months</option>
                <option value="last_year">Last Year</option>
                <option value="current_month">Current Month</option>
                <option value="current_year">Current Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>

          {/* Styling Configuration */}
          {formData.widgetType === 'chart' && (
            <div className="config-section">
              <h3>Styling</h3>
              
              <div className="form-group">
                <label>Color Preset</label>
                <div className="color-preset-grid">
                  {colorPresets.map(preset => (
                    <div
                      key={preset.id}
                      className={`color-preset ${selectedColorPreset === preset.id ? 'selected' : ''}`}
                      onClick={() => handleColorPresetChange(preset.id)}
                    >
                      <div className="preset-colors">
                        {preset.colors.slice(0, 4).map((color, index) => (
                          <div
                            key={index}
                            className="preset-color"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <span className="preset-name">{preset.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Modal Actions */}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={!formData.widgetName || selectedKPIs.length === 0}
            >
              {widget ? 'Update Widget' : 'Create Widget'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WidgetConfigurationModal;
